/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { deviceIntegrationsDeleteConfig } from "../funcs/deviceIntegrationsDeleteConfig.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteDeviceIntegrationConfigRequest,
  DeleteDeviceIntegrationConfigSecurity,
} from "../models/operations/deletedeviceintegrationconfig.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteDeviceIntegrationConfigMutationVariables = {
  request: DeleteDeviceIntegrationConfigRequest;
  security?: DeleteDeviceIntegrationConfigSecurity | undefined;
  options?: RequestOptions;
};

export type DeleteDeviceIntegrationConfigMutationData = void;

export type DeleteDeviceIntegrationConfigMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteConfig deviceIntegrations
 *
 * @remarks
 * Disconnect the org-wide device integration for a provider. Synced inventory becomes invisible to coverage; reconnecting starts fresh.
 */
export function useDeleteDeviceIntegrationConfigMutation(
  options?: MutationHookOptions<
    DeleteDeviceIntegrationConfigMutationData,
    DeleteDeviceIntegrationConfigMutationError,
    DeleteDeviceIntegrationConfigMutationVariables
  >,
): UseMutationResult<
  DeleteDeviceIntegrationConfigMutationData,
  DeleteDeviceIntegrationConfigMutationError,
  DeleteDeviceIntegrationConfigMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteDeviceIntegrationConfigMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteDeviceIntegrationConfig(): MutationKey {
  return ["@gram/client", "deviceIntegrations", "deleteConfig"];
}

export function buildDeleteDeviceIntegrationConfigMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteDeviceIntegrationConfigMutationVariables,
  ) => Promise<DeleteDeviceIntegrationConfigMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteDeviceIntegrationConfig(),
    mutationFn: function deleteDeviceIntegrationConfigMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteDeviceIntegrationConfigMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(deviceIntegrationsDeleteConfig(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
